import {
  computed,
  readonly,
  shallowRef,
  toValue,
  type MaybeRefOrGetter,
} from "vue";
import { resolveNavigationTarget } from "#bb/lib/navigationTarget";
import { resolveFileApiPath } from "#bb/lib/pathContract";
import { waitForAutomationRun } from "#bb/lib/pdfReportDownload";

// Navigation waits for the run so the next page renders its computed data, but
// never strands the user if the run is slow or the status check fails.
const RUN_WAIT_POLL_MS = 500;
const RUN_WAIT_TIMEOUT_MS = 30_000;

export interface UseBlockCompletionActionOptions {
  editor: MaybeRefOrGetter<boolean>;
  automationPath: MaybeRefOrGetter<string | null | undefined>;
  navigationPath: MaybeRefOrGetter<string | null | undefined>;
  navigationDelaySeconds?: MaybeRefOrGetter<number | null | undefined>;
  beforeExecute?: () => boolean | Promise<boolean>;
  beforeNavigation?: () => void | Promise<void>;
}

const getIdToken = (session: unknown) => {
  if (!session || typeof session !== "object" || !("tokens" in session)) {
    throw new Error("No ID token is available. User is not logged in.");
  }

  const tokens = session.tokens;
  if (
    !tokens ||
    typeof tokens !== "object" ||
    !("id_token" in tokens) ||
    typeof tokens.id_token !== "string"
  ) {
    throw new Error("No ID token is available. User is not logged in.");
  }

  return tokens.id_token;
};

export const useBlockCompletionAction = (
  options: UseBlockCompletionActionOptions
) => {
  const features = inject<{
    resourceDefinitionPath?: string | null;
  }>("render-features", {});
  const { profile } = useProfile();
  const route = useRoute();
  const { siteId } = useSite();
  const { resolveHref } = useAppRoute();
  const busy = shallowRef(false);

  const automationPath = computed(() => toValue(options.automationPath)?.trim() ?? "");
  const automationFileApiPath = computed(() =>
    resolveFileApiPath(automationPath.value, {
      userId: profile.value?.id,
      label: "Automation path",
      resourceDefinitionPath: features.resourceDefinitionPath,
    })
  );

  const navigationTarget = computed(() =>
    resolveNavigationTarget(toValue(options.navigationPath), {
      currentPath: route.fullPath,
      resourceDefinitionPath: features.resourceDefinitionPath,
      query: { user: profile.value?.id },
      resolvePath: resolveHref,
    })
  );

  const runAutomation = async () => {
    const resolvedSiteId = siteId.value;
    if (!resolvedSiteId) return;

    const { session } = useUserSession();
    return useApiFetch<{ runId?: string }>("/v1/automations/run", {
      method: "POST",
      body: {
        options: {
          path: automationFileApiPath.value,
          inputs: {
            userId: profile.value?.id,
            siteId: resolvedSiteId,
            accessToken: getIdToken(session.value),
          },
          siteId: resolvedSiteId,
        },
      },
    });
  };

  const waitForRun = async (runId: string) => {
    try {
      await waitForAutomationRun(runId, {
        pollIntervalMs: RUN_WAIT_POLL_MS,
        timeoutMs: RUN_WAIT_TIMEOUT_MS,
        load: (id) =>
          useApiFetch<{ status: string; errorMessage?: string | null }>(
            `/v1/automations/runs/${encodeURIComponent(id)}/status`,
          ),
      });
    } catch {
      // Proceed with navigation; results will load on the next page as before.
    }
  };

  const execute = async () => {
    if (toValue(options.editor)) return false;

    busy.value = true;
    try {
      if (options.beforeExecute && !(await options.beforeExecute())) {
        return false;
      }

      let runId: string | undefined;
      if (automationPath.value) {
        runId = (await runAutomation())?.runId;
      }

      if (navigationTarget.value?.href) {
        if (runId) await waitForRun(runId);
        await options.beforeNavigation?.();

        const delay = (toValue(options.navigationDelaySeconds) ?? 0) * 1000;
        if (delay > 0) {
          await new Promise<void>((resolve) => setTimeout(resolve, delay));
        }

        await navigateTo(navigationTarget.value.href, {
          external: navigationTarget.value.external,
        });
      }

      return true;
    } finally {
      busy.value = false;
    }
  };

  return {
    busy: readonly(busy),
    navigationTarget,
    execute,
  };
};
